import { fmtDate, ymd } from './datetime';

/**
 * 내 강의실 — 서버(`/members/me/enrollments/*`)가 주는 모양과 순수 계산.
 * 클라이언트 부품이 함께 쓰므로 **서버 전용 모듈을 물지 않는다**(`lib/site.ts` 를 import 하면
 * `next/headers` 가 끌려와 500 이 난다 — 후기 화면에서 이미 겪었다).
 */
export type EnrollStatus = 'WAITING' | 'IN_PROGRESS' | 'PAUSED' | 'EXPIRED' | 'CANCELED';
export type CourseType = 'FREEPASS' | 'PACKAGE' | 'LECTURE' | 'BOOK' | 'SUBSCRIPTION';

/** 내 수강 한 줄 — 목록·종료 강좌 화면이 그린다 */
export interface MyEnrollment {
  cid: number;
  productId: number;
  productType: CourseType;
  name: string;
  image: string | null;
  status: EnrollStatus;
  startDate: string | null;
  endDate: string | null;
  classStartDate: string | null;
  progress: number;
  certificateThreshold: number;
  favorite: boolean;
  teacherNames: string[];
  lessonCount: number;
  completedLessons: number;
  lastStudiedAt: string | null;
  /**
   * 어디서 온 수강인가 — 구매(주문 있음) / 부여(관리자·무료) / **구독**(계약, 수강 행 없음).
   * 부여는 연장·일시정지가 없고, 구독은 그 둘에 더해 수강확인서도 없다.
   */
  source: 'ORDER' | 'GRANT' | 'BILLING';
  /** 구독으로 여는 줄이면 계약 번호 — 주소가 강좌 상세로 간다(강의실이 아니다) */
  bmid?: number;
  extend: { canApply: boolean; countLeft: number; daysLeft: number };
  pause: { canApply: boolean; active: boolean; countLeft: number; daysLeft: number; startDate: string | null; endDate: string | null };
  certificateReady: boolean;
}

export interface EnrollmentList { rows: MyEnrollment[]; total: number; page: number; size: number }

/**
 * 나의 현황 + 하단 공지(`GET dashboard`).
 *
 * **PC 카드와 모바일 칸이 같은 목록**이다(2026-09-08 사용자 확정). 무엇을 어떤 순서로 그릴지는
 * 서버가 `order` 로 준다 — 값이 없는 칸(구독 없음·게시판 없음)은 서버가 이미 빼고 준다.
 * PC 는 [최근 본 강의] 를 맨 앞에 고정으로 두고 `order` 를 이어 붙여 최대 다섯이다.
 */
export type StatusKey = 'study' | 'total' | 'qna' | 'device';

/** 정기구독 — 나의 현황이 아니라 **별도 섹션**이다(2026-09-08 사용자 확정). 없으면 그 줄 자체가 없다 */
export interface SubscriptionLine {
  planName: string;
  endDate: string | null;
  dday: number | null;
  /** 남은 날이 사흘 이하 — 화면이 붉게 그린다 */
  warn: boolean;
}

export interface Dashboard {
  /** 그릴 순서 — 최근 본 강의는 여기 없다(맨 앞 고정). 누적은 모바일에만 온다 */
  order: StatusKey[];
  subscription: SubscriptionLine | null;
  /** 학습현황 — PC 카드는 오늘과 누적을 두 줄로 담는다 */
  study: { today: { lessons: number; seconds: number }; total: { lessons: number; seconds: number } };
  /** 문의 — 1:1 과 선생님 문의를 한 칸에. 보이는 것은 더 최근 글 하나다 */
  qna: {
    total: number; answered: number;
    latest: { boardId: number; postId: number; subject: string | null; kind: 'help' | 'tq'; answered: boolean } | null;
  } | null;
  /** 기기 등록 — 값이 언제나 있다. 한도는 구독이 있으면 구독 값이 이긴다 */
  device: {
    pc: { used: number; limit: number | null };
    mobile: { used: number; limit: number | null };
    resetLeft: number | null;
  };
  recent: {
    /** 일반 수강이면 수강권 번호, 구독이면 0 — 갈래는 `bmid` 로 안다 */
    enrollmentId: number; name: string; progress: number;
    /** 구독 시청이면 계약 번호와 진입 상품 — [이어보기] 가 구독 재생 주소를 이 둘로 만든다 */
    bmid: number | null; upid: number | null;
    /** 그 차시가 속한 단과 — [이어보기] 가 재생 주소를 만들 때 쓴다(패키지면 구성 단과) */
    productId: number;
    curriculumId: number; lessonNo: number | null; lessonName: string; position: number; runtime: number | null; watchedAt: string | null;
  } | null;
  notices: Array<{ id: number; subject: string | null; createdAt: string; isNotice: boolean }>;
}

/** 강의실 상세(`GET :id`) */
export interface ClassroomLesson {
  ccid: number;
  cnum: number | null;
  cname: string;
  rtime: number | null;
  showVtype: string;
  free: boolean;
  currentPosition: number;
  playbackDuration: number;
  progress: number;
  done: boolean;
  file: string | null;
  hasSubtitle: boolean;
  /** 차시 소개 — 플레이어 우측 패널에서 펼쳐 본다(없으면 null) */
  summary: string | null;
  /** 포스터 — 플레이어의 '다음 강의' 레이어가 쓴다(없으면 null) */
  poster: string | null;
  lastStudiedAt: string | null;
  /** 오픈일·마감일과 서버가 판정한 상태 — 예정·마감이면 [스트리밍][다운로드] 자리에 날짜 배지 */
  opensAt: string | null;
  closesAt: string | null;
  state: OpenState;
  /**
   * 차시별 재생 제한 — **걸린 강좌에만**(없으면 null). 초 단위.
   * 서버가 재생 차단 엔진과 **같은 함수**로 셈해 준다 — 화면이 다시 계산하면 갈린다.
   */
  limit: { used: number; cap: number } | null;
}
export type OpenState = 'SCHEDULED' | 'OPEN' | 'CLOSED';
export interface ClassroomLecture {
  productId: number;
  name: string;
  visible: boolean;
  /** 회원이 표시한 강좌(bm-btn) — '선택 강좌만 보기' 가 이걸로 거른다. 노출(`visible`)과 다르다 */
  bookmarked: boolean;
  classDetailId: number | null;
  opensAt: string | null;
  closesAt: string | null;
  state: OpenState;
  /** 강사 — 각자의 Q&A 게시판을 들고 온다. `qnaBoardId` 가 null 이면 받는 분이 될 수 없다 */
  teachers: Array<{ id: number; name: string | null; qnaBoardId: number | null }>;
  lessonCount: number;
  completedLessons: number;
  progress: number;
  curriculum: ClassroomLesson[];
}
export interface Classroom {
  cid: number;
  /** 구독으로 열었으면 그 계약 번호 — 일반 수강에는 없다(갈래를 화면이 이걸로 안다) */
  bmid?: number;
  productId: number;
  productType: CourseType;
  /**
   * 산 단가의 기기 구분 — 단가를 PC·모바일로 나눠 파는 마켓에서는 이 값이 **수강 가능 기기**다.
   * 못 보는 기기에서는 [스트리밍]·[다운로드] 를 감추고 강좌 머리에 까닭을 적는다(2026-09-17 사용자 확정).
   */
  platform: 'ALL' | 'PC' | 'MOBILE';
  status: EnrollStatus;
  startDate: string | null;
  endDate: string | null;
  favorite: boolean;
  directClass: boolean;
  /** 수강 제한 스냅샷(`tb_class.study_limit`) — 상품 목록의 `studyLimit` 과 같은 모양 */
  studyLimit: {
    lecture?: { mode: 'RELATIVE' | 'FIXED'; times?: number; seconds?: number };
    lesson?: { mode: 'RELATIVE' | 'FIXED'; times?: number; seconds?: number };
    daily?: { lectures?: number; lessons?: number };
  } | null;
  totalProgress: number;
  lessonCount: number;
  completedLessons: number;
  stats: { todayLessons: number; todaySeconds: number; totalSeconds: number };
  product: {
    name: string; image: string | null; profile: string | null; certificateThreshold: number; reviewEnabled: boolean;
    teachers: Array<{ id: number; name: string | null }>;
    /** 걸린 분류 전부 — 각각 [상위, 하위] 순 */
    categoryPaths: Array<Array<{ id: number; name: Record<string, string> | null }>>;
  };
  certificateReady: boolean;
  /** 질문할 강사가 없을 때 갈 곳(1:1 문의) — 없으면 [질문하기] 를 그리지 않는다 */
  helpBoardId: number | null;
  lectures: ClassroomLecture[];
}

/** 연장 견적(`GET :id/extension-quote?days=`) — 결제 화면도 같은 값을 받는다 */
export interface ExtensionQuote {
  cid: number;
  productId: number;
  productType: CourseType;
  productName: string;
  image: string | null;
  marketId: number;
  days: number;
  dailyFee: number;
  amount: number;
  currentEndDate: string | null;
  newEndDate: string | null;
  countLeft: number;
  daysLeft: number;
}

/** 뷰 기억 쿠키 — 후기(`sp_review_view`)와 같은 방식. 서버가 첫 그림부터 반영한다 */
export const MYCLASS_VIEW_COOKIE = 'sp_myclass_view';
export type MyclassView = 'list' | 'card';

/** 달력 날짜(`@db.Date`)까지 남은 날 — **KST 오늘** 기준. 지났으면 음수 */
export function daysUntil(date: string | null | undefined): number | null {
  if (!date) return null;
  const target = Date.UTC(...splitYmd(ymd(date)));
  const today = Date.UTC(...splitYmd(fmtDate(new Date())));
  return Math.round((target - today) / 86_400_000);
}

/** 달력 날짜에 n 일 더하기 — 종료일 셈(일시정지·연장 미리보기) */
export function plusDays(date: string, n: number): string {
  const d = new Date(Date.UTC(...splitYmd(ymd(date))));
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function splitYmd(s: string): [number, number, number] {
  const [y, m, d] = s.split('-').map(Number);
  return [y, m - 1, d];
}

/** 초 → `H:MM:SS`(1시간 미만이면 `M:SS`) — 차시 줄의 재생 위치·러닝타임 */
export function clock(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.floor(seconds ?? 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

/** 초 → 시·분 — 통계 칸("1시간 24분")이 단위 글자를 따로 그린다 */
export function hoursMinutes(seconds: number): { h: number; m: number } {
  const s = Math.max(0, Math.floor(seconds));
  return { h: Math.floor(s / 3600), m: Math.floor((s % 3600) / 60) };
}

/**
 * 진도 바 색 — **늘 메인 컬러**(`rate-fill-high`, 2026-09-07 사용자 확정 "진도율 색깔은 메인컬러로 고정").
 * 가이드의 빨강·노랑(`rate-fill-low/mid`)은 쓰지 않는다 — 진도가 낮다고 경고색을 칠 일이 아니다.
 */
export const rateClass = (_pct: number) => 'rate-fill rate-fill-high';
